import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ComponentType, useCallback, useMemo, useRef, useState} from 'react';
import {
  ReactFlow,
  Controls,
  EdgeChange,
  EdgeProps,
  EdgeSelectionChange,
  NodeChange,
  NodeProps,
  NodeSelectionChange,
  ReactFlowProvider,
  Transform,
  useReactFlow,
  useStoreApi,
  useViewport,
  XYPosition,
} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {Method, ProcedureMaterialOutputSetting} from 'procedures/ProcedureDomainTypes';
import {ProcedureFlowchart, ProcedureNodeType, ProcedureEdgeType} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import * as StubbableFunctions from 'processes/graph-builder-processes/flowchart-viz/util/stubbableFunctions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

import {ProcedureAddFirstMethodCard} from './buttons/ProcedureAddFirstMethodCard';
import {
  makeNewEmptyMethodNode,
  changeMethodDetails,
  sortMethodNodesByRowColumn,
  getFramedViewport,
  generateNewMethodCid,
} from './flowchartManagementUtils';
import {findMaterialOutputSetting} from './materialEdgeUtils';
import MethodEditor from './method-config/MethodEditor';
import {useMethodDragOverlay} from './methodDragOverlay/useMethodDragOverlay';
import {ProcedureEditorContext, ProcedureEditorContextValue} from './ProcedureEditorContext';
import {EDGE_END_MARKER_DEFINITIONS} from './ProcedureMarkerDefinition';
import ProcedureMaterialEdgeRenderer from './ProcedureMaterialEdgeRenderer';
import ProcedureMethodEdgeRenderer from './ProcedureMethodEdgeRenderer';
import ProcedureMethodNodeRenderer from './ProcedureMethodNodeRenderer';
import ProcedureMethodSectionFilterDropdown, {
  ALL_METHOD_SECTION_OPTIONS,
  FilteredProcedureMethodSection,
} from './ProcedureMethodSectionFilterDropdown';
import {GridLayoutContextProvider, useConvertedGridLayoutGraph} from './useConvertedGridLayoutGraph';
import {EditingMethodNameState} from './useEditingMethodNameState';
import useLegacyProcedureFlowchartEditorCustomCallbacks from './useLegacyProcedureFlowchartEditorCustomCallbacks';
import useLegacyProcedureMaterialEdgeConnection from './useLegacyProcedureMaterialEdgeConnection';
import {ModifyFlowchartCallback} from './useProcedureActions';
import {useProcedureFlowchartEditorSelection} from './useProcedureFlowchartEditorSelection';

/* Converts a point in the react flow coordinate system into the client's viewport X-Y pixel position.
 *
 * Copied from reactflow source: https://github.com/wbkd/react-flow/blob/main/packages/core/src/utils/graph.ts
 * When react flow v12 is released, switch to importing the appropriate function.
 * See https://github.com/wbkd/react-flow/issues/3193
 */
function rendererPointToPoint({x, y}: XYPosition, [tx, ty, tScale]: Transform): XYPosition {
  return {
    x: x * tScale + tx,
    y: y * tScale + ty,
  };
}

export const PROCEDURE_EDITOR_NODE_TYPES: Record<ProcedureNodeType, ComponentType<NodeProps>> = {
  [ProcedureNodeType.METHOD]: ProcedureMethodNodeRenderer,
};

export const PROCEDURE_EDITOR_EDGE_TYPES: Record<ProcedureEdgeType, ComponentType<EdgeProps>> = {
  [ProcedureEdgeType.METHOD_EDGE]: ProcedureMethodEdgeRenderer,
  [ProcedureEdgeType.MATERIAL_EDGE]: ProcedureMaterialEdgeRenderer,
};

function ProcedureFlowchartEditor(props: {
  reactFlowContainerWidth: number;
  reactFlowContainerHeight: number;
  flowchart: ProcedureFlowchart;
  modifyFlowchart: (cb: ModifyFlowchartCallback) => void;
  startEditMethod: (nodeId: string) => void;
  stopEditMethod: () => void;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  isProcedureReadOnly: boolean;
  procedureId: string;
  editingMethodNameState: EditingMethodNameState;
}): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const reactFlowInstance = useReactFlow();
  const reactFlowStore = useStoreApi();
  const {x: viewportX, y: viewportY, zoom: viewportZoom} = useViewport();
  const {modifyFlowchart, reactFlowWrapper, isProcedureReadOnly} = props;
  const {nodes, edges} = props.flowchart;

  const {accessLevel, procedureMetadata} = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;

  const [invalidMaterialHandles, setInvalidMaterialHandles] = useState<Array<string>>([]);

  const {editingNameMethodCid, setEditingNameMethodCid, autofocusEditNameOnNewMethod} =
    props.editingMethodNameState;
  const [selectedMethodSectionFilter, setSelectedMethodSectionFilter] =
    useState<Array<FilteredProcedureMethodSection>>(ALL_METHOD_SECTION_OPTIONS);
  const [materialOutputSettingBeingConnected, setMaterialOutputSettingBeingConnected] =
    useState<ProcedureMaterialOutputSetting | null>(null);

  // Transform a point in react flow's coordinate space to an equivalent point in the client coordinate space
  const reactFlowPointToClientPoint = useCallback(
    (reactFlowPoint: XYPosition): XYPosition =>
      rendererPointToPoint(reactFlowPoint, reactFlowStore.getState().transform),
    [reactFlowStore]
  );
  // Transform a point the client coordinate space to an equivalent point in react flow's coordinate space
  const clientPointToReactFlowPoint = useCallback(
    (clientPoint: XYPosition): XYPosition => reactFlowInstance.project(clientPoint),
    [reactFlowInstance]
  );

  const {methodNodeCallbacks, methodEdgeCallbacks, materialEdgeCallbacks} =
    useLegacyProcedureFlowchartEditorCustomCallbacks({
      modifyFlowchart,
      startEditMethod: props.startEditMethod,
      autofocusEditNameOnNewMethod,
    });

  const {convertedProcedureGraph, nodeCidToComputedCanvasPos, maxHeightByRow} = useConvertedGridLayoutGraph(
    props.flowchart,
    methodNodeCallbacks,
    methodEdgeCallbacks,
    materialEdgeCallbacks,
    {
      clientPointToCanvasPoint: clientPointToReactFlowPoint,
      canvasPointToClientPoint: reactFlowPointToClientPoint,
      clientOriginPos: {x: viewportX, y: viewportY},
    }
  );

  const {dragOverlayComponent, methodCidBeingDragged, handleNodeDrag, handleNodeDragStop} =
    useMethodDragOverlay(
      props.flowchart,
      modifyFlowchart,
      reactFlowWrapper,
      reactFlowPointToClientPoint,
      nodeCidToComputedCanvasPos,
      maxHeightByRow,
      viewportZoom
    );

  const {
    handleNodeSelectionChanges,
    handleEdgeSelectionChanges,
    handleDeleteSelection,
    convertedProcedureGraphWithSelected,
  } = useProcedureFlowchartEditorSelection(
    props.flowchart,
    modifyFlowchart,
    convertedProcedureGraph,
    props.stopEditMethod
  );

  const handleNodesChange = useCallback(
    (changes: Array<NodeChange>): void => {
      const selectionChanges = changes.filter((c): c is NodeSelectionChange => c.type === 'select');
      handleNodeSelectionChanges(selectionChanges);
    },
    [handleNodeSelectionChanges]
  );

  const handleEdgesChange = useCallback(
    (changes: Array<EdgeChange>): void => {
      const selectionChanges = changes.filter((c): c is EdgeSelectionChange => c.type === 'select');
      handleEdgeSelectionChanges(selectionChanges);
    },
    [handleEdgeSelectionChanges]
  );

  const {handleConnectStart, handleConnectEnd, handleConnect, materialEdgeConnectionLine} =
    useLegacyProcedureMaterialEdgeConnection(
      props.flowchart,
      modifyFlowchart,
      materialOutputSettingBeingConnected,
      reactFlowWrapper
    );

  const handlesInUse = useMemo(() => {
    const handleIdsBeingUsed: Set<string> = new Set();
    for (const edge of edges) {
      handleIdsBeingUsed.add(edge.sourceHandle);
      handleIdsBeingUsed.add(edge.targetHandle);
    }
    return [...handleIdsBeingUsed];
  }, [edges]);

  const editorContextValue: ProcedureEditorContextValue = useMemo(
    () => ({
      handlesInUse,
      methodNamesInUse: objectFromEntries(props.flowchart.nodes.map((n) => [n.cid, n.name])),
      invalidMaterialHandles,
      setInvalidMaterialHandles,
      editingNameMethodCid,
      setEditingNameMethodCid,
      methodCidBeingDragged,
      selectedMethodSectionFilter,
      isProcedureReadOnly,
      numberOfMethods: props.flowchart.nodes.length,
      materialOutputSettingBeingConnected,
    }),
    [
      editingNameMethodCid,
      handlesInUse,
      invalidMaterialHandles,
      setInvalidMaterialHandles,
      methodCidBeingDragged,
      setEditingNameMethodCid,
      selectedMethodSectionFilter,
      isProcedureReadOnly,
      materialOutputSettingBeingConnected,
      props.flowchart.nodes,
    ]
  );

  const defaultViewport = useMemo(
    () => getFramedViewport(props.flowchart, props.reactFlowContainerWidth, props.reactFlowContainerHeight),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (nodes.length === 0) {
    return (
      <div className={css(styles.procedureGetStartedButtun)}>
        <ProcedureAddFirstMethodCard
          onClick={() => {
            const newNode = makeNewEmptyMethodNode(procedureType, 300, 300, generateNewMethodCid());
            modifyFlowchart((fc) => ({edges: fc.edges, nodes: [...fc.nodes, newNode]}));
            autofocusEditNameOnNewMethod(newNode.cid);
          }}
        />
      </div>
    );
  }

  return (
    <ProcedureEditorContext.Provider value={editorContextValue}>
      <ProcedureMethodSectionFilterDropdown
        procedureId={props.procedureId}
        selectedMethodSectionFilter={selectedMethodSectionFilter}
        setSelectedMethodSectionFilter={setSelectedMethodSectionFilter}
        filterableSectionOptions={ALL_METHOD_SECTION_OPTIONS}
      />
      <ReactFlowWrapper
        width={props.reactFlowContainerWidth}
        containerWidthForControls={props.reactFlowContainerWidth}
        onKeyDown={(e: ElementKeyboardEvent) => {
          if (keyboard.isADeleteKey(e) && !isProcedureReadOnly) {
            handleDeleteSelection();
            e.preventDefault();
          }
        }}
      >
        <ReactFlow
          {...defaultReactFlowProps}
          nodesConnectable={!isProcedureReadOnly}
          nodesDraggable={!isProcedureReadOnly && accessLevel === ProcedureAccessLevel.FULL_ACCESS}
          selectNodesOnDrag={false}
          elementsSelectable={true}
          multiSelectionKeyCode={['Meta', 'ShiftLeft', 'ShiftRight']}
          nodes={convertedProcedureGraphWithSelected.nodes}
          edges={convertedProcedureGraphWithSelected.edges}
          nodeTypes={PROCEDURE_EDITOR_NODE_TYPES}
          edgeTypes={PROCEDURE_EDITOR_EDGE_TYPES}
          onNodesChange={handleNodesChange}
          onNodeDragStop={handleNodeDragStop}
          onNodeDrag={editingNameMethodCid == null ? handleNodeDrag : undefined}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          onConnectStart={(event, {nodeId, handleId}) => {
            setMaterialOutputSettingBeingConnected(
              nodeId == null || handleId == null
                ? null
                : findMaterialOutputSetting(props.flowchart, nodeId, handleId)
            );
            handleConnectStart();
          }}
          onConnectEnd={() => {
            setMaterialOutputSettingBeingConnected(null);
            handleConnectEnd();
          }}
          connectionLineComponent={materialEdgeConnectionLine}
          panOnScroll={true}
          panOnDrag={true}
          selectionOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={true}
          defaultViewport={defaultViewport}
        >
          {EDGE_END_MARKER_DEFINITIONS}
          <Controls className={css(styles.controls)} showInteractive={false} />
        </ReactFlow>
      </ReactFlowWrapper>
      {dragOverlayComponent}
    </ProcedureEditorContext.Provider>
  );
}

interface ProcedureEditorProps {
  flowchart: ProcedureFlowchart;
  modifyFlowchart: (cb: ModifyFlowchartCallback) => void;
  procedureId: string;
  templateCollectionId: string;
  isProcedureReadOnly: boolean;
  isProcedureArchived: boolean;
  /**
   * procedureEditorKey is used to force ProcedureEditor to re-render and flush its internal state. It is
   * passed as an explicit prop rather than using key so that it can be read and passed into child components.
   */
  procedureEditorKey: number;
  onStopEditingMethod: () => void;
  onStartEditingMethod: (methodCid: string) => void;
  editingMethodNameState: EditingMethodNameState;
}

export type MethodUpdater = (originalMethod: Method) => Method;

export default function LegacyProcedureEditor(props: ProcedureEditorProps): JSX.Element {
  const {flowchart, modifyFlowchart, procedureEditorKey, onStopEditingMethod, onStartEditingMethod} = props;
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const {width: reactFlowContainerWidth, height: reactFlowContainerHeight} =
    StubbableFunctions.useResizeObserver({
      ref: reactFlowWrapper,
    });

  const {legacySelectedMethodCid} = ProcedurePageContext.useContext();

  const onChangeMethod = useCallback(
    (cid: string, methodUpdater: MethodUpdater): void => {
      modifyFlowchart((fc) => {
        const originalMethodData = fc.nodes.find((n) => n.cid === cid);
        assert(originalMethodData != null, 'Unable to find the method to change');
        return changeMethodDetails(fc, methodUpdater(originalMethodData));
      });
    },
    [modifyFlowchart]
  );

  const sortedMethodNodes = useMemo(
    () => sortMethodNodesByRowColumn(props.flowchart.nodes),
    [props.flowchart.nodes]
  );

  const selectedMethod =
    legacySelectedMethodCid != null
      ? sortedMethodNodes.find(({cid}) => cid === legacySelectedMethodCid)
      : null;
  if (legacySelectedMethodCid) {
    assert(selectedMethod != null, 'Selected unit operation must exist');
  }

  return (
    <div className={css(styles.wrapper)}>
      {selectedMethod ? (
        <div className={css(styles.methodEditorWrapper)}>
          <MethodEditor
            // MethodEditor should re-render if either the selected method changes or ProcedurePage
            // manually triggers it when resetting draft changes
            key={`${legacySelectedMethodCid}_${procedureEditorKey}`}
            selectedMethod={selectedMethod}
            onChangeMethod={onChangeMethod}
            isProcedureReadOnly={props.isProcedureReadOnly}
            isProcedureArchived={props.isProcedureArchived}
            allMethodNames={flowchart.nodes.map((m) => ({cid: m.cid, name: m.name}))}
          />
        </div>
      ) : (
        <div
          ref={reactFlowWrapper}
          className={css(styles.flowchartEditorWrapper)}
          {...getTestProps({component: 'ProcedureEditor', element: 'flowchartEditor'})}
        >
          <ReactFlowProvider key={procedureEditorKey}>
            {/* ReactFlowProvider needs to re-render if draft changes are reset from ProcedurePage */}
            <GridLayoutContextProvider>
              {reactFlowContainerWidth != null && reactFlowContainerHeight != null && (
                <ProcedureFlowchartEditor
                  reactFlowContainerWidth={reactFlowContainerWidth}
                  reactFlowContainerHeight={reactFlowContainerHeight}
                  startEditMethod={onStartEditingMethod}
                  stopEditMethod={onStopEditingMethod}
                  reactFlowWrapper={reactFlowWrapper}
                  {...props}
                />
              )}
            </GridLayoutContextProvider>
          </ReactFlowProvider>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100vh',
    background: GRAY_LIGHTER,
    position: 'relative',
    overflow: 'hidden',
  },
  flowchartEditorWrapper: {
    width: '100%',
    height: '100%',
    zIndex: 0,
    position: 'relative',
  },
  methodEditorWrapper: {
    width: '100%',
    height: '100%',
    zIndex: 1, // Covers the flowchart editor
    position: 'relative',
  },
  controls: {
    left: '1px',
  },
  procedureGetStartedButtun: {
    top: '200px',
    left: '40px',
    position: 'absolute',
  },
});
